'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Coffee, LayoutDashboard, BookOpen, DollarSign,
  Package, ChevronLeft, ChevronRight, LogOut, Store
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  label: string;
  labelFr: string;
  href: string;
  icon: React.ElementType;
  isStub?: boolean;
}

const navItems: NavItem[] = [
  { label: 'لوحة التحكم', labelFr: 'Tableau de Bord', href: '/cafe-restaurant', icon: LayoutDashboard },
  { label: 'الوصفات', labelFr: 'Recettes', href: '/cafe-restaurant/recipes', icon: BookOpen, isStub: true },
  { label: 'المواد الأولية', labelFr: 'Ingrédients', href: '/cafe-restaurant/ingredients', icon: Package, isStub: true },
  { label: 'تكاليف الإنتاج', labelFr: 'Coûts Production', href: '/cafe-restaurant/production-cost', icon: DollarSign, isStub: true },
];

export default function CafeLayout({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  const isActive = (href: string) => {
    if (href === '/cafe-restaurant') return pathname === '/cafe-restaurant';
    return pathname.startsWith(href);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Sidebar */}
      <aside
        className="flex-shrink-0 bg-card border-r border-border flex flex-col h-screen sticky top-0 z-30 transition-all duration-200"
        style={{ width: collapsed ? '56px' : '220px' }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-4 border-b border-border min-h-[56px]">
          <div className="w-8 h-8 rounded-lg bg-amber-600 flex items-center justify-center flex-shrink-0">
            <Coffee size={16} className="text-white" />
          </div>
          {!collapsed && (
            <div className="overflow-hidden flex-1">
              <span className="font-bold text-sm text-foreground block truncate">مقهى / مطعم</span>
              <span className="text-2xs text-muted-foreground block">Café / Restaurant</span>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground flex-shrink-0"
          >
            {collapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
          </button>
        </div>

        {/* Phase 2 badge */}
        {!collapsed && (
          <div className="px-3 pt-2 pb-1">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
              <span className="text-amber-600 text-xs">🚧</span>
              <div className="flex-1 min-w-0">
                <p className="text-2xs font-semibold text-amber-800">المرحلة الثانية</p>
                <p className="text-2xs text-amber-600">Phase 2 — En développement</p>
              </div>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
          {navItems.map(item => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                href={item.href}
                title={collapsed ? item.labelFr : undefined}
                className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all relative group ${
                  active
                    ? 'bg-amber-600 text-white' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <Icon size={15} className="flex-shrink-0" />
                {!collapsed && (
                  <span className="flex-1 truncate flex items-center gap-1.5">
                    {item.label}
                    {item.isStub && (
                      <span className="text-2xs px-1 py-0.5 bg-amber-100 text-amber-600 rounded font-normal">قريباً</span>
                    )}
                  </span>
                )}
                {collapsed && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-foreground text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                    {item.labelFr}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="border-t border-border p-2 space-y-0.5">
          <Link
            href="/retailer"
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs text-muted-foreground hover:bg-muted transition-colors relative group"
            title={collapsed ? 'Épicerie / Hanout' : undefined}
          >
            <Store size={15} className="flex-shrink-0" />
            {!collapsed && <span>← حانوت / بقالة</span>}
          </Link>
          <Link
            href="/"
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs text-muted-foreground hover:bg-muted transition-colors relative group"
            title={collapsed ? 'Retour Fournisseur' : undefined}
          >
            <LogOut size={15} className="flex-shrink-0" />
            {!collapsed && <span>← واجهة المورد</span>}
          </Link>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="h-14 bg-card border-b border-border flex items-center px-4 gap-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-amber-600 flex items-center justify-center">
              <Coffee size={13} className="text-white" />
            </div>
            <span className="font-semibold text-sm text-foreground">مقهى / مطعم — Café / Restaurant</span>
          </div>
          <div className="flex-1" />
          <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-1 rounded-full font-semibold">
            🚧 المرحلة الثانية — Phase 2
          </span>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-screen-2xl mx-auto px-4 lg:px-6 py-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
